<template>
  <div class="pi-filters" role="group" aria-label="Filtros do Product Intelligence">
    <div class="pi-filters__row">
      <div v-if="has('period')" class="pi-filters__field pi-filters__period">
        <span class="pi-filters__label pi-filters__labelled">
          <span :id="ids.period">Período</span>
          <pi-definition-tooltip info-key="filter.period" label="Período"/>
        </span>
        <div class="pi-seg" role="group" :aria-labelledby="ids.period">
          <button v-for="preset in presets" :key="preset.value" type="button" class="pi-seg__btn"
                  :class="{ 'is-on': !customOpen && period.preset === preset.value }"
                  :aria-pressed="!customOpen && period.preset === preset.value ? 'true' : 'false'"
                  @click="choosePreset(preset.value)">{{ preset.label }}</button>
          <button type="button" class="pi-seg__btn" :class="{ 'is-on': customOpen }"
                  :aria-pressed="customOpen ? 'true' : 'false'" @click="openCustom">Personalizado</button>
        </div>
        <date-range-filter v-if="customOpen" class="pi-filters__range" label="Intervalo" :from="value.from" :to="value.to"
                           @update:from="setRange($event, value.to)" @update:to="setRange(value.from, $event)"/>
        <span class="pi-filters__hint">{{ periodText }}</span>
        <span v-if="period.error" class="pi-filters__error" role="alert">
          {{ period.error }} Mostrando os últimos {{ period.days }} dias.
        </span>
      </div>

      <span v-if="has('compare')" class="pi-filters__labelled">
        <label class="pi-filters__check">
          <input type="checkbox" :checked="compareOn" @change="setCompare($event.target.checked)">
          Comparar com o período anterior
          <span v-if="compareOn && previousText" class="pi-filters__hint">({{ previousText }})</span>
        </label>
        <pi-definition-tooltip info-key="filter.compare" label="Comparar com o período anterior"/>
      </span>
    </div>

    <div class="pi-filters__row">
      <div v-if="has('company')" class="pi-filters__field pi-company">
        <span class="pi-filters__label pi-filters__labelled">
          <label class="pi-filters__label" :for="ids.company">Empresa</label>
          <pi-definition-tooltip info-key="filter.company" label="Empresa"/>
        </span>
        <div v-if="value.companyId" class="pi-chip">
          <span class="pi-chip__text">{{ value.companyName || value.companyId }}</span>
          <button type="button" class="pi-chip__remove" :aria-label="'Remover o filtro de empresa ' + (value.companyName || value.companyId)"
                  @click="clearCompany">×</button>
        </div>
        <div v-else class="pi-combo">
          <input :id="ids.company" v-model="companyTerm" type="search" class="form-control pi-input pi-combo__input"
                 maxlength="60" placeholder="Nome ou id (2+ letras)" autocomplete="off" role="combobox"
                 aria-autocomplete="list" :aria-expanded="companyOpen ? 'true' : 'false'" :aria-controls="ids.companyList"
                 :aria-activedescendant="activeOptionId"
                 @input="onCompanyInput" @keydown.down.prevent="moveActive(1)" @keydown.up.prevent="moveActive(-1)"
                 @keydown.enter.prevent="chooseActive" @keydown.esc="closeCompany" @blur="closeCompany">
          <ul v-show="companyOpen" :id="ids.companyList" class="pi-combo__list" role="listbox" aria-label="Empresas encontradas">
            <li v-if="companyLoading" class="pi-combo__info" role="option" aria-disabled="true" aria-selected="false">Buscando…</li>
            <li v-else-if="companyError" class="pi-combo__info" role="option" aria-disabled="true" aria-selected="false">{{ companyError }}</li>
            <li v-else-if="!companyResults.length" class="pi-combo__info" role="option" aria-disabled="true" aria-selected="false">
              Nenhuma empresa encontrada.
            </li>
            <template v-else>
              <li v-for="(company, index) in companyResults" :id="optionId(index)" :key="company.companyId + company.shardCode"
                  class="pi-combo__option" :class="{ 'is-active': index === activeIndex }" role="option"
                  :aria-selected="index === activeIndex ? 'true' : 'false'"
                  @mousedown.prevent="chooseCompany(company)">
                {{ company.name }}
                <span v-if="company.shardCode" class="pi-combo__sub">{{ company.shardCode }}</span>
              </li>
            </template>
          </ul>
        </div>
      </div>

      <filter-select v-if="has('module')" label="Módulo" :value="value.module" :options="moduleOptions"
                     @input="set('module', $event)">
        <pi-definition-tooltip slot="info" class="pi-filters__info" info-key="filter.module" label="Módulo"/>
      </filter-select>
      <div v-if="has('featureType')" class="pi-filters__field">
        <filter-select label="Funcionalidade" :value="value.featureType" :options="featureOptionsShown" all-label="Todas"
                       @input="set('featureType', $event)">
          <pi-definition-tooltip slot="info" class="pi-filters__info" info-key="filter.featureType" label="Funcionalidade"/>
        </filter-select>
        <span v-if="featureError" class="pi-filters__error">{{ featureError }}</span>
      </div>
      <filter-select v-if="has('roleClass')" label="Papel do usuário" :value="value.roleClass" :options="roleClassOptions"
                     @input="set('roleClass', $event)">
        <pi-definition-tooltip slot="info" class="pi-filters__info" info-key="filter.roleClass" label="Papel do usuário"/>
      </filter-select>
      <filter-select v-if="has('platform')" label="Plataforma" :value="value.platform" :options="platformOptions"
                     all-label="Todas" @input="set('platform', $event)">
        <pi-definition-tooltip slot="info" class="pi-filters__info" info-key="filter.platform" label="Plataforma"/>
      </filter-select>
      <filter-select v-if="has('sizeBand')" label="Porte (funcionários)" :value="value.sizeBand" :options="sizeBandOptions"
                     @input="set('sizeBand', $event)">
        <pi-definition-tooltip slot="info" class="pi-filters__info" info-key="filter.sizeBand" label="Porte (funcionários)"/>
      </filter-select>
      <div v-if="has('segment')" class="pi-filters__field">
        <span class="pi-filters__label pi-filters__labelled">
          <label class="pi-filters__label" :for="ids.segment">Perfil da empresa</label>
          <pi-definition-tooltip info-key="filter.segment" label="Perfil da empresa"/>
        </span>
        <input :id="ids.segment" type="text" class="form-control pi-input pi-filters__segment" maxlength="60"
               placeholder="A, B, C, D ou E" :value="value.segment"
               @change="set('segment', $event.target.value.trim())" @keydown.enter.prevent="$event.target.blur()">
      </div>
      <filter-select v-if="has('granularity')" label="Granularidade" :value="value.granularity"
                     :options="granularityOptions" all-label="Diária" @input="set('granularity', $event)">
        <pi-definition-tooltip slot="info" class="pi-filters__info" info-key="filter.granularity" label="Granularidade"/>
      </filter-select>

      <router-link v-if="hasCuts" :to="categoriesLink" class="pi-filters__help">O que é cada corte?</router-link>
      <a v-if="active" href="#" class="pi-filters__clear" @click.prevent="$emit('clear')">Limpar filtros</a>
    </div>

    <p v-if="ignored.length" class="pi-filters__ignored">
      Não se aplica a esta visão: {{ ignored.join(' · ') }}
    </p>
    <p v-if="platformNoteText" class="pi-filters__note">{{ platformNoteText }}</p>
  </div>
</template>

<script>
import dateRangeFilter from "../default/dateRangeFilter.vue";
import filterSelect from "../default/filterSelect.vue";
import { hasAnyFilter } from "../default/listFilters.js";
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import { loadFeatureOptions, searchCompanies, errorMessage } from "./piApi.js";
import { formatPeriod } from "./piFormat.js";
import { categoryLink } from "./piCategoriesModel.js";
import {
  GRANULARITY_OPTIONS, MODULE_OPTIONS, PERIOD_PRESETS, PLATFORM_OPTIONS, ROLE_CLASS_OPTIONS, SIZE_BAND_FILTER_OPTIONS,
  applyCompare, applyCustomRange, applyPreset, compareEnabled, ignoredFilters, platformNote, previousPeriod, resolvePeriod,
} from "./piQuery.js";

const SEARCH_DELAY_MS = 350;
const MIN_SEARCH_LENGTH = 2;

let sequence = 0;

export default {
  name: "pi-filters",
  components: { dateRangeFilter, filterSelect, piDefinitionTooltip },
  props: {
    value: { type: Object, required: true },
    fields: { type: Array, default: () => ["period", "compare"] },
  },
  data() {
    sequence += 1;
    const prefix = "pi-filters-" + sequence;
    return {
      ids: {
        period: prefix + "-period",
        company: prefix + "-company",
        companyList: prefix + "-company-list",
        segment: prefix + "-segment",
      },
      customRequested: false,
      companyTerm: "",
      companyResults: [],
      companyOpen: false,
      companyLoading: false,
      companyError: "",
      activeIndex: -1,
      searchTimer: null,
      searchSeq: 0,
      featureOptions: [],
      featureError: "",
      presets: PERIOD_PRESETS,
      moduleOptions: MODULE_OPTIONS,
      roleClassOptions: ROLE_CLASS_OPTIONS,
      platformOptions: PLATFORM_OPTIONS,
      sizeBandOptions: SIZE_BAND_FILTER_OPTIONS,
      granularityOptions: GRANULARITY_OPTIONS.filter((option) => option.value !== "day"),
    };
  },
  computed: {
    hasCuts() {
      return ["module", "roleClass", "platform", "sizeBand", "segment"].some((field) => this.has(field));
    },
    categoriesLink() {
      return categoryLink("");
    },
    period() {
      return resolvePeriod(this.value, new Date());
    },
    customOpen() {
      return this.customRequested || !!this.value.from || !!this.value.to;
    },
    periodText() {
      return formatPeriod(this.period.from, this.period.to) + " (" + this.period.days + " dias)";
    },
    compareOn() {
      return compareEnabled(this.value);
    },
    previousText() {
      const previous = previousPeriod(this.period);
      return previous ? formatPeriod(previous.from, previous.to) : "";
    },
    active() {
      return hasAnyFilter(this.value);
    },
    ignored() {
      return ignoredFilters(this.value, this.fields);
    },
    platformNoteText() {
      return this.has("platform") ? platformNote(this.value.platform) : "";
    },
    activeOptionId() {
      return this.companyOpen && this.activeIndex >= 0 && this.activeIndex < this.companyResults.length
        ? this.optionId(this.activeIndex)
        : null;
    },
    featureOptionsShown() {
      const selected = this.value.featureType;
      if (!selected || this.featureOptions.some((option) => option.value === selected)) return this.featureOptions;
      return this.featureOptions.concat([{ value: selected, label: "Funcionalidade " + selected }]);
    },
  },
  created() {
    if (this.has("featureType")) this.loadFeatures();
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchSeq += 1;
  },
  methods: {
    has(field) {
      return this.fields.indexOf(field) !== -1;
    },
    emit(next) {
      this.$emit("input", next);
    },
    set(key, raw) {
      const patch = {};
      patch[key] = raw === null || raw === undefined ? "" : String(raw);
      this.emit(Object.assign({}, this.value, patch));
    },
    choosePreset(preset) {
      this.customRequested = false;
      this.emit(applyPreset(this.value, preset));
    },
    openCustom() {
      this.customRequested = true;
      if (!this.value.from && !this.value.to) this.emit(applyCustomRange(this.value, this.period.from, this.period.to));
    },
    setRange(from, to) {
      this.emit(applyCustomRange(this.value, from, to));
    },
    setCompare(enabled) {
      this.emit(applyCompare(this.value, enabled));
    },
    loadFeatures() {
      this.featureError = "";
      loadFeatureOptions()
        .then((options) => {
          this.featureOptions = options;
        })
        .catch((error) => {
          this.featureOptions = [];
          this.featureError = errorMessage(error, "carregar as funcionalidades");
        });
    },
    optionId(index) {
      return this.ids.companyList + "-" + index;
    },
    onCompanyInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchSeq += 1;
      const term = this.companyTerm.trim();
      this.activeIndex = -1;
      if (term.length < MIN_SEARCH_LENGTH) {
        this.companyOpen = false;
        this.companyResults = [];
        this.companyLoading = false;
        this.companyError = "";
        return;
      }
      this.companyOpen = true;
      this.companyLoading = true;
      this.companyError = "";
      this.searchTimer = setTimeout(() => this.runSearch(term), SEARCH_DELAY_MS);
    },
    runSearch(term) {
      const seq = this.searchSeq;
      searchCompanies(term, this.period)
        .then((rows) => {
          if (seq !== this.searchSeq) return;
          this.companyResults = rows;
          this.companyLoading = false;
          this.activeIndex = rows.length ? 0 : -1;
        })
        .catch((error) => {
          if (seq !== this.searchSeq) return;
          this.companyResults = [];
          this.companyLoading = false;
          this.companyError = errorMessage(error, "buscar empresas");
        });
    },
    moveActive(step) {
      if (!this.companyOpen || !this.companyResults.length) return;
      const count = this.companyResults.length;
      this.activeIndex = (this.activeIndex + step + count) % count;
    },
    chooseActive() {
      if (this.activeIndex >= 0 && this.activeIndex < this.companyResults.length) {
        this.chooseCompany(this.companyResults[this.activeIndex]);
      }
    },
    chooseCompany(company) {
      this.closeCompany();
      this.companyTerm = "";
      this.emit(Object.assign({}, this.value, { companyId: company.companyId, companyName: company.name }));
    },
    closeCompany() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchSeq += 1;
      this.companyOpen = false;
      this.companyLoading = false;
      this.activeIndex = -1;
    },
    clearCompany() {
      this.emit(Object.assign({}, this.value, { companyId: "", companyName: "" }));
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-filters {
  margin-bottom: 18px;
  padding: 12px 14px;
  border: 1px solid #e6e7eb;
  border-radius: 6px;
  background: #fafbfc;
}

.pi-filters__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 20px;
}

.pi-filters__row + .pi-filters__row {
  margin-top: 12px;
}

.pi-filters__row:empty {
  display: none;
}

.pi-filters__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pi-filters__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #4e5560;
}

.pi-filters__labelled {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pi-filters__labelled .pi-filters__label {
  margin: 0;
}

.pi-filters__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-filters__hint {
  font-size: 11px;
  color: #6b7280;
}

.pi-filters__error {
  font-size: 12px;
  color: #a3172b;
}

.pi-filters__check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #3a424e;
}

.pi-filters__clear {
  padding-bottom: 8px;
  font-size: 12px;
  color: #1b59f8;
}

.pi-filters__clear:hover,
.pi-filters__clear:focus {
  color: #1b59f8;
  text-decoration: underline;
}

.pi-filters__segment {
  width: 200px;
}

.pi-filters__ignored {
  margin: 10px 0 0;
  font-size: 11px;
  color: #7a4f00;
}

.pi-filters__note {
  margin: 10px 0 0;
  font-size: 11px;
  color: #6b7280;
}

.pi-seg {
  display: inline-flex;
  flex-wrap: wrap;
  border: 1px solid #c5cad3;
  border-radius: 4px;
  overflow: hidden;
}

.pi-seg__btn {
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-right: 1px solid #c5cad3;
  background: #ffffff;
  color: #3a424e;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.pi-seg__btn:last-child {
  border-right: 0;
}

.pi-seg__btn.is-on {
  background: #1b59f8;
  color: #ffffff;
}

.pi-filters__range {
  margin-top: 6px;
}

.pi-company {
  position: relative;
  min-width: 240px;
}

.pi-combo__input {
  width: 260px;
}

.pi-combo__list {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 40;
  width: 320px;
  max-height: 260px;
  margin: 2px 0 0;
  padding: 4px 0;
  overflow-y: auto;
  border: 1px solid #d9dde3;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  list-style: none;
}

.pi-combo__option,
.pi-combo__info {
  padding: 6px 10px;
  font-size: 12px;
}

.pi-combo__option {
  cursor: pointer;
}

.pi-combo__option.is-active,
.pi-combo__option:hover {
  background: #eef3fe;
  color: #1340b5;
}

.pi-combo__info {
  color: #6b7280;
}

.pi-combo__sub {
  margin-left: 6px;
  font-size: 11px;
  color: #6b7280;
}

.pi-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 300px;
  height: 34px;
  padding: 0 6px 0 10px;
  border: 1px solid #b9cefb;
  border-radius: 17px;
  background: #eef3fe;
  font-size: 12px;
  color: #1a45b8;
}

.pi-chip__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pi-chip__remove {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #1a45b8;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.pi-chip__remove:hover {
  background: #dbe6fd;
}

@media (max-width: 767px) {
  .pi-filters__row {
    flex-direction: column;
    align-items: stretch;
  }

  .pi-company,
  .pi-combo__input,
  .pi-combo__list,
  .pi-filters__segment {
    width: 100%;
    min-width: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piFilters.vue